import { alpha, createTheme } from '@mui/material/styles';

const FONT_STACK = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO_STACK = '"Roboto Mono", "SFMono-Regular", Consolas, monospace';

const palettes = {
  dark: {
    background: { default: '#0e0d18', paper: '#171522' },
    text: { primary: '#f7f5ff', secondary: '#aaa6b8', disabled: '#6f6a7f' },
    divider: 'rgba(255, 255, 255, 0.105)',
    surface: '#171522',
    elevated: '#1e1b2b',
    soft: '#211f2d',
    input: 'rgba(255, 255, 255, 0.045)',
    nav: 'rgba(8, 7, 19, 0.82)',
  },
  light: {
    background: { default: '#f5f4fa', paper: '#ffffff' },
    text: { primary: '#10101a', secondary: '#686678', disabled: '#9995a6' },
    divider: 'rgba(17, 16, 27, 0.105)',
    surface: '#ffffff',
    elevated: '#ffffff',
    soft: '#eceaf4',
    input: 'rgba(17, 16, 27, 0.035)',
    nav: 'rgba(255, 255, 255, 0.84)',
  },
};

const status = {
  primary: { main: '#7657ff', light: '#a28fff', dark: '#5b3ee0', contrastText: '#ffffff' },
  secondary: { main: '#ff714f', light: '#ff987e', dark: '#d94f32', contrastText: '#ffffff' },
  success: { main: '#41d9ab', light: '#75e8c5', dark: '#169b74', contrastText: '#071510' },
  warning: { main: '#ffb454', light: '#ffcc87', dark: '#cf7920', contrastText: '#1b1004' },
  error: { main: '#ff5e78', light: '#ff8da0', dark: '#d63652', contrastText: '#ffffff' },
  info: { main: '#6f7fff', light: '#9aa5ff', dark: '#4855d8', contrastText: '#ffffff' },
};

const transitions = '180ms cubic-bezier(0.2, 0, 0, 1)';

export const createAppTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';
  const colors = palettes[mode] || palettes.dark;
  const border = colors.divider;
  const cardShadow = isDark
    ? '0 24px 70px rgba(0, 0, 0, 0.28)'
    : '0 24px 60px rgba(34, 25, 75, 0.10)';

  return createTheme({
    palette: {
      mode,
      ...status,
      background: colors.background,
      text: colors.text,
      divider: border,
      action: {
        hover: alpha(status.primary.main, isDark ? 0.12 : 0.075),
        selected: alpha(status.primary.main, isDark ? 0.18 : 0.11),
        disabled: colors.text.disabled,
        disabledBackground: alpha(colors.text.disabled, 0.12),
      },
    },
    vitria: {
      violet: '#7657ff',
      coral: '#ff714f',
      mint: '#41d9ab',
      night: '#080713',
      surface: colors.surface,
      elevated: colors.elevated,
      soft: colors.soft,
      input: colors.input,
      nav: colors.nav,
      border,
      cardShadow,
      monoFont: MONO_STACK,
    },
    shape: { borderRadius: 18 },
    shadows: [
      'none',
      isDark ? '0 10px 30px rgba(0,0,0,.18)' : '0 10px 28px rgba(34,25,75,.07)',
      cardShadow,
      ...Array(22).fill(cardShadow),
    ],
    typography: {
      fontFamily: FONT_STACK,
      h1: { fontSize: 'clamp(2.65rem, 5vw, 5.2rem)', fontWeight: 800, lineHeight: 0.98, letterSpacing: '-0.062em' },
      h2: { fontSize: 'clamp(2.2rem, 4vw, 4rem)', fontWeight: 800, lineHeight: 1.02, letterSpacing: '-0.052em' },
      h3: { fontSize: 'clamp(1.9rem, 3.3vw, 3rem)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.045em' },
      h4: { fontSize: 'clamp(1.55rem, 2.4vw, 2.2rem)', fontWeight: 760, lineHeight: 1.14, letterSpacing: '-0.035em' },
      h5: { fontSize: '1.25rem', fontWeight: 750, lineHeight: 1.25, letterSpacing: '-0.025em' },
      h6: { fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.018em' },
      subtitle1: { fontWeight: 650, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 700, letterSpacing: '0.01em' },
      body1: { lineHeight: 1.65, letterSpacing: '-0.006em' },
      body2: { lineHeight: 1.55, letterSpacing: '-0.002em' },
      caption: { lineHeight: 1.45, letterSpacing: '0.015em' },
      overline: { fontSize: '0.69rem', fontWeight: 800, lineHeight: 1.5, letterSpacing: '0.16em' },
      button: { textTransform: 'none', fontWeight: 750, letterSpacing: '-0.01em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: mode,
            '--vitria-violet': status.primary.main,
            '--vitria-coral': status.secondary.main,
            '--vitria-mint': status.success.main,
          },
          'html, body, #root': { minHeight: '100%' },
          html: { backgroundColor: colors.background.default, scrollBehavior: 'smooth' },
          body: {
            backgroundColor: colors.background.default,
            color: colors.text.primary,
            fontFamily: FONT_STACK,
            WebkitFontSmoothing: 'antialiased',
            textRendering: 'optimizeLegibility',
          },
          '::selection': { backgroundColor: alpha(status.primary.main, 0.34), color: colors.text.primary },
          'a, button, input, textarea, select': { WebkitTapHighlightColor: 'transparent' },
          ':focus-visible': { outline: `3px solid ${alpha(status.primary.main, 0.42)}`, outlineOffset: 3 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            color: colors.text.primary,
            backgroundImage: 'none',
            backgroundColor: colors.nav,
            borderBottom: `1px solid ${border}`,
            backdropFilter: 'blur(22px) saturate(140%)',
            WebkitBackdropFilter: 'blur(22px) saturate(140%)',
          },
        },
      },
      MuiToolbar: { styleOverrides: { root: { minHeight: 72 } } },
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 44,
            paddingInline: 20,
            borderRadius: 999,
            transition: `transform ${transitions}, box-shadow ${transitions}, background-color ${transitions}, border-color ${transitions}`,
            '&:hover': { transform: 'translateY(-2px)' },
            '&:active': { transform: 'translateY(0)' },
          },
          containedPrimary: {
            background: 'linear-gradient(105deg, #7657ff, #5f40ea)',
            boxShadow: '0 12px 30px rgba(94, 63, 232, 0.28)',
            '&:hover': { background: 'linear-gradient(105deg, #8165ff, #6949ee)', boxShadow: '0 17px 38px rgba(94, 63, 232, 0.38)' },
          },
          containedSecondary: {
            background: 'linear-gradient(105deg, #ff815f, #e25638)',
            boxShadow: '0 12px 30px rgba(226, 86, 56, 0.24)',
          },
          outlined: { borderColor: border, backgroundColor: alpha(colors.surface, isDark ? 0.34 : 0.7), '&:hover': { borderColor: alpha(status.primary.main, 0.58), backgroundColor: alpha(status.primary.main, 0.08) } },
          text: { '&:hover': { backgroundColor: alpha(status.primary.main, 0.09) } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 13,
            transition: `transform ${transitions}, background-color ${transitions}, color ${transitions}`,
            '&:hover': { transform: 'translateY(-1px)', backgroundColor: alpha(status.primary.main, 0.12) },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 24 },
          elevation1: { border: `1px solid ${border}`, boxShadow: cardShadow },
          elevation2: { border: `1px solid ${border}`, boxShadow: cardShadow },
          elevation3: { border: `1px solid ${border}`, boxShadow: cardShadow },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${border}`,
            borderRadius: 24,
            backgroundImage: isDark
              ? 'linear-gradient(145deg, rgba(255,255,255,.025), rgba(255,255,255,0))'
              : 'linear-gradient(145deg, #ffffff, #fbfaff)',
            boxShadow: isDark ? '0 18px 55px rgba(0,0,0,.16)' : '0 18px 50px rgba(34,25,75,.065)',
            transition: `transform ${transitions}, border-color ${transitions}, box-shadow ${transitions}`,
            '&:hover': { borderColor: alpha(status.primary.main, 0.26), boxShadow: cardShadow },
          },
        },
      },
      MuiCardHeader: { styleOverrides: { root: { padding: '24px 24px 10px' }, title: { fontWeight: 750, letterSpacing: '-0.025em' }, subheader: { color: colors.text.secondary } } },
      MuiCardContent: { styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } } },
      MuiCardActions: { styleOverrides: { root: { padding: '0 24px 24px', gap: 8 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: 50,
            borderRadius: 15,
            backgroundColor: colors.input,
            transition: `background-color ${transitions}, box-shadow ${transitions}`,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(status.primary.main, 0.44) },
            '&.Mui-focused': { backgroundColor: alpha(colors.surface, isDark ? 0.58 : 0.92), boxShadow: `0 0 0 4px ${alpha(status.primary.main, 0.11)}` },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: status.primary.main, borderWidth: 1 },
          },
          input: { padding: '14px 16px' },
          multiline: { padding: 0 },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: colors.text.secondary, '&.Mui-focused': { color: status.primary.light } } } },
      MuiFormHelperText: { styleOverrides: { root: { marginLeft: 4, marginTop: 7 } } },
      MuiSelect: { styleOverrides: { select: { display: 'flex', alignItems: 'center' } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, height: 30, fontWeight: 700, borderColor: border },
          filled: { backgroundColor: alpha(status.primary.main, isDark ? 0.16 : 0.1) },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: border } } },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 46 },
          indicator: { height: 3, borderRadius: '3px 3px 0 0', background: 'linear-gradient(90deg, #7657ff, #ff714f)' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { minHeight: 46, textTransform: 'none', fontWeight: 700, color: colors.text.secondary, '&.Mui-selected': { color: colors.text.primary } },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { border: `1px solid ${border}`, borderRadius: 20, backgroundColor: alpha(colors.surface, isDark ? 0.62 : 0.9) } } },
      MuiTableHead: { styleOverrides: { root: { backgroundColor: alpha(status.primary.main, isDark ? 0.095 : 0.055) } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: border, padding: '15px 18px' },
          head: { color: colors.text.secondary, fontSize: '0.71rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' },
        },
      },
      MuiTableRow: { styleOverrides: { root: { transition: `background-color ${transitions}`, '&:last-child td': { borderBottom: 0 }, '&:hover': { backgroundColor: alpha(status.primary.main, 0.045) } } } },
      MuiDialog: { styleOverrides: { paper: { border: `1px solid ${border}`, borderRadius: 28, backgroundColor: colors.elevated, backgroundImage: 'none', boxShadow: '0 36px 100px rgba(0,0,0,.42)' } } },
      MuiDialogTitle: { styleOverrides: { root: { padding: '26px 28px 10px', fontSize: '1.35rem', fontWeight: 760, letterSpacing: '-0.03em' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '18px 28px 22px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '0 28px 26px', gap: 8 } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(5, 5, 11, 0.72)', backdropFilter: 'blur(5px)' } } },
      MuiMenu: { styleOverrides: { paper: { marginTop: 8, minWidth: 210, padding: 7, border: `1px solid ${border}`, borderRadius: 18, backgroundColor: alpha(colors.elevated, 0.96), backdropFilter: 'blur(22px)', boxShadow: '0 24px 65px rgba(0,0,0,.28)' } } },
      MuiMenuItem: { styleOverrides: { root: { minHeight: 42, borderRadius: 12, marginBlock: 2, fontSize: '0.875rem', '&.Mui-selected': { backgroundColor: alpha(status.primary.main, 0.15) } } } },
      MuiDrawer: { styleOverrides: { paper: { borderColor: border, backgroundColor: colors.elevated, backgroundImage: 'none' } } },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 14, transition: `background-color ${transitions}, color ${transitions}`, '&.Mui-selected': { backgroundColor: alpha(status.primary.main, 0.14) } } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 16, border: '1px solid', alignItems: 'center' }, standardSuccess: { borderColor: alpha(status.success.main, 0.28) }, standardError: { borderColor: alpha(status.error.main, 0.28) }, standardWarning: { borderColor: alpha(status.warning.main, 0.28) }, standardInfo: { borderColor: alpha(status.info.main, 0.28) } } },
      MuiTooltip: { styleOverrides: { tooltip: { border: `1px solid ${border}`, borderRadius: 10, backgroundColor: isDark ? '#29253a' : '#171522', color: '#f7f5ff', fontSize: '0.72rem', boxShadow: '0 12px 30px rgba(0,0,0,.2)' }, arrow: { color: isDark ? '#29253a' : '#171522' } } },
      MuiAvatar: { styleOverrides: { root: { background: 'linear-gradient(145deg, #8063ff, #5134c7)', color: '#ffffff', fontWeight: 750, border: `1px solid ${alpha('#ffffff', 0.16)}` } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: alpha(colors.text.secondary, 0.11), borderRadius: 12 } } },
      MuiLinearProgress: { styleOverrides: { root: { height: 7, borderRadius: 999, backgroundColor: alpha(status.primary.main, 0.1) }, bar: { borderRadius: 999, background: 'linear-gradient(90deg, #7657ff, #ff714f)' } } },
      MuiSwitch: { styleOverrides: { root: { padding: 8 }, switchBase: { '&.Mui-checked + .MuiSwitch-track': { backgroundColor: status.primary.main, opacity: 1 } }, track: { borderRadius: 999, backgroundColor: colors.text.secondary, opacity: 0.24 } } },
      MuiPaginationItem: { styleOverrides: { root: { borderRadius: 11, fontWeight: 650, '&.Mui-selected': { color: '#fff', background: 'linear-gradient(145deg, #7657ff, #5b3ee0)' } } } },
      MuiCircularProgress: { defaultProps: { thickness: 3.6 } },
    },
  });
};
